import { Button, Flex, Progress, Space } from 'antd';
import { RcFile } from 'antd/es/upload';
import { UploadFile } from 'antd/lib';
import imageCompression from 'browser-image-compression';
import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useMount } from '../../hook/useMount';
import { useSockJSMessageRecordRef } from '../../hook/useSockJS';
import { DisabledButton } from '../DisabledButton';
import { FallbackComponent } from '../Fallback';
import {
  CheckedCircle,
  CloseIcon,
  JpgFileIcon,
  PdfFileIcon,
  PngFileIcon,
} from '../Icon';
import { Modal } from '../Modal';
import { UploadFileComponent } from '../UploadFile';
import {
  AttachmentsFile,
  IFileRendering,
  IManageUploadStatus,
  IRcFileManage,
  IUploadAttachment,
  UploadStatus,
} from './interface';
import {
  useCheckFile,
  useDeleteUploadFiles,
  useUploadMultipleFiles,
} from './selectors';

/**
 * File types accepted for upload.
 */
const Accept = '.PDF, .JPEG, .JPG, .PNG';
/**
 * Upload file size limit in MB.
 */
const LIMIT_MB = 10;
// const LIMIT_SIZE = LIMIT_MB * 1024 * 1024; // MB * 1024KB * 1024Bytes

/**
 * Upload modal component to handle multiple file uploads with progress,
 * duplicate checks, compression (for images), and retry logic.
 */
export const UploadAttachment: FC<IUploadAttachment> = ({
  id = '',
  isOpen,
  category,
  onClose,
  onFinished,
  remainFiles = 0,
  existedFileNames = [],
  loading = false,
}) => {
  // State management for uploads
  const [manageUploadSuccess, setManageUploadSuccess] = useState<
    IManageUploadStatus[]
  >([]);
  const [manageUploadFailed, setManageUploadFailed] = useState<
    IManageUploadStatus[]
  >([]);
  const [filesUploaded, setFilesUploaded] = useState<AttachmentsFile[]>([]);
  const [filesList, setFilesList] = useState<IRcFileManage[]>([]);
  // Subscribe to WebSocket progress messages
  const [messagesRecords] = useSockJSMessageRecordRef('upload', 'progressId');
  // Upload and delete hooks
  const [uploadData, callUploadMultipleFile, clearUploadMultipleFile] =
    useUploadMultipleFiles();
  const [deleteUploadData, callDeleteUploadFile, clearDeleteUploadFile] =
    useDeleteUploadFiles();

  /**
   * When upload completes successfully, update uploaded files list.
   */
  useEffect(() => {
    if (uploadData.data) {
      if (uploadData.data?.length > 0) {
        const formatData = {
          id: uploadData.data[0].id,
          name: uploadData.data[0].name,
          originalName: uploadData.data[0].originalName,
          extension: uploadData.data[0].extension,
          url: uploadData.data[0].url,
          size: uploadData.data[0].size,
          relativePath: uploadData.data[0].relativePath,
        };
        setFilesUploaded((prev) => {
          const newList = [...prev];
          newList.push(formatData);
          return newList;
        });
      }
      clearUploadMultipleFile();
    }
  }, [clearUploadMultipleFile, uploadData.data, manageUploadSuccess]);

  /**
   * Clears delete response on file deletion.
   */
  useEffect(() => {
    if (deleteUploadData.data) {
      clearDeleteUploadFile();
    }
  }, [clearDeleteUploadFile, deleteUploadData.data]);

  /**
   * Remove file from upload lists and trigger deletion if necessary.
   * @param file The file to remove
   */
  const removeFile = useCallback(
    (file: UploadFile) => {
      const itemRemove = manageUploadSuccess.find(
        (f) => f.progressId === file.uid
      );
      if (itemRemove) {
        callDeleteUploadFile(
          {
            category,
            filenames: [itemRemove.filename],
          },
          undefined,
          undefined,
          Math.random().toString()
        );
      }
      setManageUploadSuccess((prev) => {
        const newList = [...prev].filter((f) => f.progressId !== file.uid);
        return newList;
      });
      setManageUploadFailed((prev) => {
        const newList = [...prev].filter((f) => f.progressId !== file.uid);
        return newList;
      });
      setFilesList((prev) => {
        const newList = [...prev].filter((f) => f.uid !== file.uid);
        return newList;
      });
    },
    [callDeleteUploadFile, manageUploadSuccess]
  );

  /**
   * Handle file submission including compression if it's an image.
   * @param file File to be uploaded
   */
  const onSubmitFiles = useCallback(
    async (files: RcFile) => {
      try {
        const isImage = files.type.startsWith('image/');
        let fileToUpload: File = files;

        // Compress images before upload
        if (isImage) {
          const compressedBlob = await imageCompression(files, {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            initialQuality: 0.8,
            useWebWorker: true,
          });

          // 👇 Wrap the compressed blob as a File to keep the name and type
          fileToUpload = new File([compressedBlob], files.name, {
            type: compressedBlob.type,
          });
        }

        const formData = new FormData();
        formData.append('files', fileToUpload);
        formData.append('fileCategory', category);
        formData.append('objId', id);
        formData.append('progressId', files.uid);

        callUploadMultipleFile(
          formData,
          undefined,
          undefined,
          Math.random().toString()
        );
      } catch (error) {
        console.error('Compression/upload error:', error);
      }
    },
    [callUploadMultipleFile, id, category]
  );

  /**
   * Clear all state variables back to their initial values.
   * This function is used by `handleCancel` and `handleFinished` to reset the component state.
   */
  const handleClearState = () => {
    setManageUploadSuccess([]);
    setManageUploadFailed([]);
    setFilesList([]);
    setFilesUploaded([]);
  };

  /**
   * Cancels the upload process and resets component state.
   * This function clears all the state variables and triggers the `onClose` callback
   * to close the modal or component where the upload process is being managed.
   */
  const handleCancel = () => {
    handleClearState();
    onClose();
  };

  const handleFinished = () => {
    handleClearState();
    onFinished(filesUploaded);
  };

  return (
    <Modal
      destroyOnClose
      open={isOpen}
      onCancel={handleCancel}
      zIndex={15}
      width={940}
      title="Upload File"
      maskClosable={false}
      okText="Save"
      footer={() => (
        <Flex gap={8} justify="flex-end">
          <Button onClick={handleCancel} variant="outlined">
            Cancel
          </Button>
          <DisabledButton
            disabled={
              filesList.length === 0 ||
              manageUploadSuccess.length !== filesList.length ||
              manageUploadFailed.length !== 0
            }
            type="primary"
            variant="filled"
            onClick={handleFinished}
          >
            Save
          </DisabledButton>
        </Flex>
      )}
    >
      <FallbackComponent overlay requesting={loading}>
        <UploadFileComponent
          accept={Accept}
          limitMB={LIMIT_MB}
          filesList={filesList}
          setFilesList={setFilesList}
          remainFiles={remainFiles}
          validateDuplicated={(file) =>
            existedFileNames.includes(file.name.toLowerCase()) ||
            filesList
              .map((f) => f.name.toLowerCase())
              .includes(file.name.toLowerCase())
          }
          onSubmitFiles={onSubmitFiles}
          removeFile={removeFile}
          counterSuccess={manageUploadSuccess.length}
        >
          {(fileLocal) => (
            <FileRendering
              key={fileLocal.uid}
              removeFile={removeFile}
              file={fileLocal}
              callbackOnDone={(
                fileId?: string,
                filename?: string,
                progressId?: string
              ) => {
                if (
                  fileId &&
                  progressId &&
                  filename &&
                  !manageUploadSuccess.find(
                    (item) =>
                      item.fileId === fileId &&
                      item.progressId === progressId &&
                      item.filename === filename
                  )
                ) {
                  setManageUploadSuccess((prev) => {
                    const newList = [...prev];
                    newList.push({ fileId, progressId, filename });
                    return newList;
                  });
                }
              }}
              callbackOnError={(
                fileId?: string,
                filename?: string,
                progressId?: string
              ) => {
                if (
                  fileId &&
                  progressId &&
                  filename &&
                  !manageUploadFailed.find(
                    (item) =>
                      item.fileId === fileId &&
                      item.progressId === progressId &&
                      item.filename === filename
                  )
                ) {
                  setManageUploadFailed((prev) => {
                    const newList = [...prev];
                    newList.push({ fileId, progressId, filename });
                    return newList;
                  });
                }
              }}
              message={messagesRecords?.[fileLocal.uid]}
            />
          )}
        </UploadFileComponent>
      </FallbackComponent>
    </Modal>
  );
};

const MAX_RETRY_CHECK_FILE = 3;

const FileRendering: FC<IFileRendering> = memo(
  ({ file, message, removeFile, callbackOnDone, callbackOnError }) => {
    const [, callCheckFile] = useCheckFile();
    const [, setUpdated] = useState(0);
    const [triggerFailed, setTriggerFailed] = useState(false);
    const receiveMessageRef = useRef(0);
    const percentRef = useRef<number | undefined>(0);
    const retryCheckFileRef = useRef(0);

    useMount(() => {
      setTimeout(() => {
        if (receiveMessageRef.current == 0) {
          percentRef.current = Math.floor(Math.random() * (15 - 3 + 1)) + 3;
          setUpdated(Math.random());
        }
      }, 500);
    });
    useEffect(() => {
      let runCheckFile: NodeJS.Timeout | undefined;
      if (message) {
        receiveMessageRef.current += 1;

        if (!message?.percent || parseInt(message?.percent) < 100) {
          runCheckFile = setInterval(() => {
            if (retryCheckFileRef.current > MAX_RETRY_CHECK_FILE) {
              clearInterval(runCheckFile);
              setTriggerFailed(true);
              return;
            }
            if (message?.fileId) {
              callCheckFile(undefined, undefined, { fileId: message?.fileId });
            }
            retryCheckFileRef.current += 1;
          }, 1000 * 60);
        }
      }
      if (percentRef.current && message) {
        percentRef.current = undefined;
        setUpdated(Math.random());
      }
      return () => {
        if (runCheckFile) {
          clearInterval(runCheckFile);
          retryCheckFileRef.current = 0;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [message]);

    const percent = useMemo(() => {
      if (message?.percent) {
        return parseInt(message.percent);
      }
      return 0;
    }, [message?.percent]);

    const status = useMemo(() => {
      return message?.status === UploadStatus.UploadFailed
        ? 'exception'
        : undefined;
    }, [message?.status]);

    useEffect(() => {
      if (message?.status === UploadStatus.Uploaded) {
        callbackOnDone?.(message?.fileId, message?.name, message?.progressId);
      }
      if (message?.status === UploadStatus.UploadFailed) {
        callbackOnError?.(message?.fileId, message?.name, message?.progressId);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [message?.status, message?.fileId, message?.progressId, message?.name]);

    return (
      <Flex
        key={file.uid}
        gap={12}
        className="w-full p-2 mt-3 rounded-xl border"
        justify="flex-start"
        align="center"
      >
        <Space className="px-2">
          {file.type === 'application/pdf' && <PdfFileIcon />}
          {(file.type === 'image/jpeg' || file.type === 'image/jpg') && (
            <JpgFileIcon />
          )}
          {file.type === 'image/png' && <PngFileIcon />}
        </Space>
        <Flex className="w-full" vertical>
          <Flex justify="space-between" align="center">
            <Space size={5} className="black-80 text-sm font-medium">
              {file.name}
            </Space>
            <Button
              type="link"
              className="pr-2"
              onClick={() => {
                removeFile(file);
              }}
            >
              <CloseIcon />
            </Button>
          </Flex>
          {message?.status === UploadStatus.Uploaded ? (
            <Flex gap={4} align="center">
              <CheckedCircle width={10} height={10} />
              <span style={{ color: '#16A34A' }} className="blue-100 text-xs">
                Upload Successfully
              </span>
            </Flex>
          ) : message?.status === UploadStatus.UploadFailed || triggerFailed ? (
            <Flex gap={4} align="center">
              <CloseIcon width={10} height={10} color="#DC2626" />
              <span style={{ color: '#DC2626' }} className="blue-100 text-xs">
                Upload Failed
              </span>
            </Flex>
          ) : (
            <Progress
              percent={percentRef.current ?? percent}
              strokeColor="#EF6820"
              status={status}
            />
          )}
        </Flex>
      </Flex>
    );
  }
);
